import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import styled from '@emotion/styled';
import { LanguageIcon } from '@seedlang/icons';
import { trackMeaningExpansion, SentenceReportWrapper } from '@seedlang/hoc';
import { formatNumber, isPresent, pixify, meaningSynonyms, meaningSynonymText, meaningDetailSources } from '@seedlang/utils';
import Text from 'components/text';
import SourceList from 'components/source/source_list';
import ReportMeaningsModal from 'components/vocab/report_meanings_modal';
import WordCardRow from 'components/word_card/word_card_row';
import DictionarySentences from 'components/vocab/dictionary_sentences';
import VocabSentencePopup from 'components/vocab/vocab_sentence_popup';
import TranslationWithSource from 'components/translation_with_source';
import Modal from 'components/modal';
import { AppUI } from '@seedlang/state';

const Wrapper = styled.div`
  width: 100%;
`;

const ModalScreen = styled.div`
  display: ${props => props.hidden ? 'none' : 'block'};
  height: 100%;
  min-height: 0;
  flex: 1;
  overflow: auto;
  box-sizing: border-box;
  padding: ${props => props.mobile ? '10px' : '20px'};
`;

const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: ${props => props.centered ? 'center' : 'flex-start'};
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const MeaningModalScreen = styled.div`
  display: ${props => props.hidden ? 'none' : 'flex'};
  flex-direction: column;
  height: 100%;
`;

const SentenceCountFooter = styled.div`
  flex-shrink: 0;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #CCC;
  padding: 0 20px;
  background: #FFF;
  font-size: 14px;
`;

const RegisterLabel = styled.div`
  font-size: 12px;
  align-self: flex-start;
  flex-shrink: 0;
  color: #666;
  margin-left: 12px;
  margin-right: 8px;
`;

const SynonymSkeleton = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  .synonym-placeholder {
    width: 100px;
    height: 16px;
    border-radius: 4px;
    background: #C8C8C8;
  }
`;

const MainContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  ${props => props.expanded && `
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    margin-bottom: -6px;
  `}
`;

const ExpandedContent = styled.div`
  margin: ${props => props.dictionarySentences ? '5px -5px' : '5px 5px 5px 0'};
  padding: ${props => props.dictionarySentences ? '5px 0' : '5px 10px 5px 5px'};
  border-radius: 5px;
`;

const ListItem = styled.div`
  margin-bottom: 10px;
  text-align: left;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SentenceListItem = styled.div`
  margin-bottom: 0;
  text-align: left;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ListItemText = styled.div`
  font-size: ${props => props.fontSize};
  color: #000;
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
`;


@observer
class Meaning extends React.Component {
  @observable showSentences = false;
  @observable showReportIssue = false;
  @observable activeSentence;
  @observable sentenceCount;

  setSentenceCount = count => this.sentenceCount = count;
  sentencePage;
  setSentencePage = page => {
    this.sentencePage = page;
    this.props.onSentencePage?.(page);
  };

  @observable expanded = Boolean(this.props.defaultExpanded);

  static defaultProps = {
    fontSize: '16px',
    lineHeight: '22px',
  };
  toggleExpanded = () => {
    if (this.props.summaryOnly) return;
    if (this.props.dictionarySentences && !this.props.detailView) {
      this.showSentences = true;
      return;
    }
    if (this.props.dictionarySentences || !this.isExpandable) {
      return;
    }
    this.expanded = !this.expanded;
    if (this.expanded) {
      trackMeaningExpansion(this.props.word, this.props.meaning);
    }
  }
  getSourceText = (translationSource) => {
    const sourceText = this.props.user && translationSource.source?.textForUser
      ? translationSource.source.textForUser(this.props.user)
      : translationSource.source?.text;
    if (!sourceText) return null;
    if (translationSource.source?.displayGender && this.props.word?.genderAbbreviation) {
      return `${sourceText}${this.props.word.genderAbbreviation}`;
    }
    return sourceText;
  }

  sentenceSourcesForUser = (sentence) => {
    const sources = sentence.sources || [];
    const userLanguageId = this.props.user?.languageId || 'EN';
    const prefersBritishEnglish = userLanguageId === 'EN2' || (userLanguageId === 'EN' && this.props.user?.useBritishEnglish);
    const preferredLanguageId = prefersBritishEnglish ? 'EN2' : userLanguageId;
    const sourcesForLanguage = languageId => sources.filter(source => source.languageId === languageId);
    const englishLanguageIds = prefersBritishEnglish ? ['EN2', 'EN'] : ['EN', 'EN2'];
    const englishSources = englishLanguageIds.reduce((accumulator, languageId) => {
      return accumulator.concat(sourcesForLanguage(languageId));
    }, []);
    if (userLanguageId.startsWith('EN')) {
      const preferredSources = sourcesForLanguage(preferredLanguageId);
      if (isPresent(preferredSources)) {
        return preferredSources;
      }
      const fallbackLanguageId = preferredLanguageId === 'EN2' ? 'EN' : 'EN2';
      const fallbackSources = sourcesForLanguage(fallbackLanguageId);
      if (isPresent(fallbackSources)) {
        return fallbackSources;
      }
      return sources;
    }
    const nativeSources = sourcesForLanguage(preferredLanguageId);
    if (this.props.user?.onlyShowNativeLanguage) {
      if (isPresent(nativeSources)) {
        return nativeSources;
      }
      if (isPresent(englishSources)) {
        return englishSources;
      }
      return sources;
    }
    const selectedSources = nativeSources.concat(englishSources);
    if (isPresent(selectedSources)) {
      return selectedSources;
    }
    return sources;
  }

  @computed get translationSources() {
    const translationSources = this.props.meaning?.translationSourcesForUser(this.props.user) || [];
    const userLanguageId = this.props.user?.languageId || 'EN';
    if (userLanguageId.startsWith('EN')) {
      return translationSources;
    }
    const nativeSources = [];
    const englishSources = [];
    translationSources.forEach(translationSource => {
      if (translationSource.source?.languageId?.startsWith('EN')) {
        englishSources.push(translationSource);
      } else {
        nativeSources.push(translationSource);
      }
    });
    return nativeSources.concat(englishSources);
  }

  @computed get sourceRows() {
    const rows = [];
    this.translationSources.forEach(translationSource => {
      const text = this.getSourceText(translationSource);
      if (!text) return;
      const languageId = translationSource.source?.languageId;
      const existingRow = this.props.dictionarySentences && rows.find(row => row.languageId === languageId);
      if (existingRow) {
        existingRow.texts.push(text);
      } else {
        rows.push({ id: translationSource.id, languageId, texts: [text] });
      }
    });
    return rows;
  }

  @computed get isExpandable() {
    return this.props.dictionarySentences || this.translationSources?.length > 1 || (this.props.meaning?.sentences?.length > 0);
  }

  @computed get synonymPreviewText() {
    return meaningSynonymText(this.displaySynonyms, this.expanded, this.props.dictionarySentences);
  }

  @computed get detailSources() {
    return meaningDetailSources(this.translationSources, this.displaySynonyms, this.props.word.languageId,
      synonym => this.props.onClickWord?.(synonym));
  }

  @computed get displaySynonyms() {
    return meaningSynonyms(this.props.meaning, this.props.synonymOverride);
  }

  @computed get meaningSentences() {
    return (this.props.meaning?.sentences || [])
      .map((sentence, index) => ({ sentence, index }))
      .sort((itemA, itemB) => {
        const sentenceATextLength = (itemA.sentence?.targetText || itemA.sentence?.target_text || '').length;
        const sentenceBTextLength = (itemB.sentence?.targetText || itemB.sentence?.target_text || '').length;
        if (sentenceATextLength !== sentenceBTextLength) {
          return sentenceATextLength - sentenceBTextLength;
        }
        return itemA.index - itemB.index;
      })
      .map(item => item.sentence)
      .map(sentence => {
        const preferredSource = this.sentenceSourcesForUser(sentence)[0];
        return Object.assign(Object.create(sentence), {
          languageId: this.props.word?.languageId,
          sourceText: preferredSource?.text,
          sourceTextForUser: user => {
            if (!preferredSource) {
              return null;
            }
            if (preferredSource.textForUser) {
              return preferredSource.textForUser(user);
            }
            return preferredSource.text;
          },
          sourceTextForUserLanguageId: () => preferredSource?.languageId,
        });
      });
  }

  render() {
    if (this.props.detailView) {
      return (
        <Wrapper>
          {isPresent(this.props.meaning.register) && <RegisterLabel style={{ textAlign: 'right' }}>{this.props.meaning.register}</RegisterLabel>}
          <div style={{ borderBottom: '1px solid #CCC' }}>
            <SourceList
              word={this.props.word}
              user={this.props.user}
              translations={this.detailSources}
              preserveTranslationOrder
              showFlags
              leftAlign
              hideRetranslationIcon
              flagWrapperWidth={30}
              fontSize={this.props.fontSize}
              lineHeight={this.props.lineHeight}
              sourcePadding='10px'
            />
            {!this.props.summaryOnly && this.props.meaning?.isPreview && this.props.meaning.hasSynonyms && !this.displaySynonyms.length && (
              <SynonymSkeleton role='status' aria-label='Loading synonyms' aria-busy='true' style={{ padding: '10px' }}>
                <LanguageIcon margin='0 10px 0 0' languageId={this.props.word.languageId} />
                <span className='synonym-placeholder' aria-hidden='true' />
              </SynonymSkeleton>
            )}
          </div>
          <div>
            <DictionarySentences onSentencesLoaded={this.props.onSentencesLoaded} firstRowPaddingTop={10} showSeparators onPlaySentence={this.props.onPlaySentence} wordId={this.props.word.id} meaningId={this.props.meaning.id} fontSize={pixify(this.props.fontSize)} infinite initialSentencePage={this.props.initialSentencePage} onTotalCount={this.props.onSentenceCount} />
          </div>
          {this.props.visibleSentencesByMeaning?.[this.props.meaning.id] && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px' }}>
              <button type='button' onClick={() => this.showReportIssue = true} style={{ border: 0, background: 'transparent', color: '#888', cursor: 'pointer', padding: '6px 10px' }}>
                <i className='fa fa-flag' aria-hidden='true' /> Report Issue
              </button>
            </div>
          )}
          <ReportMeaningsModal
            open={this.showReportIssue}
            word={this.props.word}
            initialMeaning={this.props.meaning}
            visibleSentencesByMeaning={this.props.visibleSentencesByMeaning}
            onClose={() => this.showReportIssue = false}
          />
        </Wrapper>
      );
    }
    return (
      <Wrapper>
        <WordCardRow
          plainHeader={this.props.dictionarySentences}
          showTopSeparator={this.props.showTopSeparator}
          id={this.props.meaning?.id}
          expandedId={this.expanded ? this.props.meaning?.id : null}
          keepExpanded={this.props.dictionarySentences || !this.isExpandable}
          actionable={this.props.dictionarySentences && !this.props.detailView && !this.props.summaryOnly}
          disableHover={this.props.summaryOnly}
          onClick={this.toggleExpanded}
          rowMargin={this.props.rowMargin}
        >
          <HeaderWrapper centered={this.props.dictionarySentences}>
            <ContentWrapper>
              <MainContent expanded={this.expanded}>
                {
                  this.expanded ?
                    <>
                      {
                        this.sourceRows.map(row => (
                          <ListItem key={row.id}>
                            <ListItemText fontSize={this.props.fontSize}>
                              <LanguageIcon
                                margin='0 5px 0 0'
                                languageId={row.languageId}
                              />
                              <strong>{row.texts.join(', ')}</strong>
                            </ListItemText>
                          </ListItem>
                        ))
                      }
                      {
                        this.displaySynonyms.length > 0 &&
                          <ListItem>
                            <ListItemText fontSize={this.props.fontSize}>
                              <LanguageIcon
                                margin='0 5px 0 0'
                                languageId={this.props.word.languageId}
                              />
                              <span>{this.synonymPreviewText}</span>
                            </ListItemText>
                          </ListItem>
                      }
                    </>
                    :
                    <>
                      {
                        this.translationSources?.length > 0 &&
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <LanguageIcon
                              margin='0 5px 0 0'
                              languageId={this.translationSources[0].source?.languageId}
                            />
                            <Text bold
                              fontSize={pixify(this.props.fontSize)}
                              lineHeight={pixify(this.props.lineHeight)}
                              textAlign='left'
                            >
                              {this.getSourceText(this.translationSources[0])}
                            </Text>
                          </div>
                      }
                      {
                        this.displaySynonyms.length > 0 &&
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <LanguageIcon
                              margin='0 5px 0 0'
                              languageId={this.props.word.languageId}
                            />
                            <Text
                              fontSize={pixify(this.props.fontSize)}
                              lineHeight={pixify(this.props.lineHeight)}
                              textAlign='left'
                            >
                              <span>{this.synonymPreviewText}</span>
                            </Text>
                          </div>
                      }
                    </>
                }
                {!this.props.summaryOnly && this.props.meaning?.isPreview && this.props.meaning.hasSynonyms && this.displaySynonyms.length === 0 && (
                  <SynonymSkeleton role='status' aria-label='Loading synonyms' aria-busy='true'>
                    <LanguageIcon margin='0 5px 0 0' languageId={this.props.word.languageId} />
                    <span className='synonym-placeholder' aria-hidden='true' />
                  </SynonymSkeleton>
                )}
              </MainContent>
            </ContentWrapper>
            {
              isPresent(this.props.meaning.register) &&
                <RegisterLabel>
                  {this.props.meaning.register}
                </RegisterLabel>
            }
            {this.props.dictionarySentences && !this.props.summaryOnly && (
              <i className='fa fa-angle-right' aria-hidden='true' style={{ marginLeft: 12, flexShrink: 0, fontSize: 20, color: '#888' }} />
            )}
          </HeaderWrapper>
          {
            this.expanded && !this.props.summaryOnly &&
              <ExpandedContent dictionarySentences={this.props.dictionarySentences}>
                {
                  !this.props.dictionarySentences && this.meaningSentences.length > 0 && this.meaningSentences.map(sentence => (
                    <SentenceListItem key={sentence.id}>
                      <TranslationWithSource
                        background='transparent'
                        languageButtonBackground='#FFF'
                        translation={sentence}
                        padding='0 0 14px 0'
                        fontSize={pixify(this.props.fontSize)}
                        languageButtonWrapperWidth='25px'
                        languageButtonWrapperPadding='0 5px 0 0'
                        showSentenceBreakdown
                        sentenceBreakdownProps={{
                          highlightWordId: this.props.word?.id,
                          hideWordAssociationHighlight: true,
                          disableOnClick: true,
                        }}
                        user={this.props.user}
                      />
                    </SentenceListItem>
                  ))
                }
                {this.props.dictionarySentences &&
                  <DictionarySentences onSentencesLoaded={this.props.onSentencesLoaded} onPlaySentence={this.props.onPlaySentence} wordId={this.props.word.id} meaningId={this.props.meaning.id} fontSize={pixify(this.props.fontSize)} infinite={this.props.detailView} initialSentencePage={this.props.initialSentencePage} onSentencePage={this.setSentencePage} onTotalCount={this.props.detailView ? this.props.onSentenceCount : undefined} />
                }
              </ExpandedContent>
          }
        </WordCardRow>
        {this.showSentences && (
          <Modal
            showCloseIcon
            gradient
            title={this.props.word.targetTextWithDefiniteArticle || this.props.word.targetText}
            contentShouldScroll
            height={Math.min(AppUI.layout.viewportHeight - 80, 580)}
            maxWidth='600px'
            width='calc(100% - 40px)'
            padding='0'
            onBack={this.activeSentence ? () => this.activeSentence = null : undefined}
            backLabel='Back to meaning'
            onCloseModal={() => {
              this.showSentences = false;
              this.activeSentence = null;
            }}
          >
            <MeaningModalScreen hidden={Boolean(this.activeSentence)}>
              <ModalScreen mobile={AppUI.layout.isMobile}>
                <MeaningDetail {...this.props} onClickWord={word => { this.showSentences = false; this.props.onClickWord?.(word); }} initialSentencePage={this.sentencePage} detailView defaultExpanded onSentenceCount={this.setSentenceCount} onPlaySentence={sentence => this.activeSentence = sentence} />
              </ModalScreen>
              <SentenceCountFooter>
                {this.sentenceCount != null && `${formatNumber(this.sentenceCount)} ${this.sentenceCount === 1 ? 'Sentence' : 'Sentences'}`}
              </SentenceCountFooter>
            </MeaningModalScreen>
            {this.activeSentence && (
              <ModalScreen mobile={AppUI.layout.isMobile}>
                <VocabSentencePopup screen autoPlay popupSentence={this.activeSentence} wordId={this.props.word.id} />
              </ModalScreen>
            )}
          </Modal>
        )}
      </Wrapper>
    );
  }
}

const MeaningDetail = SentenceReportWrapper(Meaning);

export default Meaning;
